import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import { useSkillSources, useRebuildRegistry } from '../../hooks/useSkills';
import { SourcesTable } from '../../components/skills/SourcesTable';
import { SyncReportModal } from '../../components/skills/SyncReportModal';
import * as skillService from '../../services/skillService';
import { Spinner } from '../../components/common/Spinner';

interface AddSourceFormProps {
  onAdd: (req: any) => Promise<void>;
}

const PRESETS = [
  {
    label: 'Anthropic Skills',
    name: 'anthropic-skills',
    repo_url: 'https://github.com/anthropics/skills',
    branch: 'main',
    skills_path: 'skills/',
    trust_tier: 'auto_trusted' as const,
  },
  {
    label: 'OpenAI Skills',
    name: 'openai-skills',
    repo_url: 'https://github.com/openai/skills',
    branch: 'main',
    skills_path: 'skills/',
    trust_tier: 'auto_trusted' as const,
  },
];

const AddSourceForm: React.FC<AddSourceFormProps> = ({ onAdd }) => {
  const { darkMode } = useTheme();
  const [name, setName] = useState('');
  const [repoUrl, setRepoUrl] = useState('');
  const [branch, setBranch] = useState('main');
  const [skillsPath, setSkillsPath] = useState('skills/');
  const [trustTier, setTrustTier] = useState<'auto_trusted' | 'pending'>('pending');
  const [authToken, setAuthToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const applyPreset = (preset: typeof PRESETS[number]) => {
    setName(preset.name);
    setRepoUrl(preset.repo_url);
    setBranch(preset.branch);
    setSkillsPath(preset.skills_path);
    setTrustTier(preset.trust_tier);
    if (!open) setOpen(true);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className={`px-4 py-2 rounded-md text-sm font-medium text-white ${darkMode ? 'bg-green-700 hover:bg-green-600' : 'bg-green-600 hover:bg-green-500'}`}
      >
        + Add Source
      </button>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !repoUrl.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onAdd({
        name,
        repo_url: repoUrl,
        branch,
        skills_path: skillsPath,
        trust_tier: trustTier,
        auth_token: authToken || undefined,
      });
      setName(''); setRepoUrl(''); setBranch('main'); setSkillsPath('skills/');
      setAuthToken(''); setOpen(false);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Failed to add source');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls = `w-full px-3 py-2 rounded-md border text-sm ${
    darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'
  } focus:outline-none focus:ring-2 focus:ring-blue-500`;

  return (
    <form onSubmit={handleSubmit} className={`rounded-lg border p-4 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">Add Skill Source</h3>
        <div className="flex items-center gap-2">
          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Quick fill:</span>
          {PRESETS.map((preset) => (
            <button
              key={preset.name}
              type="button"
              onClick={() => applyPreset(preset)}
              className={`text-xs px-2.5 py-1 rounded-md font-medium transition-colors ${
                darkMode
                  ? 'bg-indigo-900/40 hover:bg-indigo-800/60 text-indigo-300 border border-indigo-700'
                  : 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Name *</label>
          <input className={inputCls} value={name} onChange={e => setName(e.target.value)} placeholder="claude-code-skills" required />
        </div>
        <div>
          <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Repo URL *</label>
          <input className={inputCls} value={repoUrl} onChange={e => setRepoUrl(e.target.value)} placeholder="https://github.com/org/repo" required />
        </div>
        <div>
          <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Branch</label>
          <input className={inputCls} value={branch} onChange={e => setBranch(e.target.value)} />
        </div>
        <div>
          <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Skills Path</label>
          <input className={inputCls} value={skillsPath} onChange={e => setSkillsPath(e.target.value)} />
        </div>
        <div>
          <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Trust Tier</label>
          <select className={inputCls} value={trustTier} onChange={e => setTrustTier(e.target.value as any)}>
            <option value="pending">Pending (review required)</option>
            <option value="auto_trusted">Auto Trusted</option>
          </select>
        </div>
        <div>
          <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Auth Token (optional, private repos)</label>
          <input className={inputCls} type="password" value={authToken} onChange={e => setAuthToken(e.target.value)} placeholder="ghp_..." />
        </div>
      </div>
      {error && <p className="text-red-500 text-xs mb-3">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={isSubmitting} className={`px-4 py-2 rounded-md text-sm text-white ${isSubmitting ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'}`}>
          {isSubmitting ? 'Adding...' : 'Add Source'}
        </button>
        <button type="button" onClick={() => setOpen(false)} className={`px-4 py-2 rounded-md text-sm ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export const SkillSources: React.FC = () => {
  const { darkMode } = useTheme();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { data: sources = [], isLoading, error, refetch } = useSkillSources();
  const [syncReport, setSyncReport] = useState<any>(null);
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const [rebuildResult, setRebuildResult] = useState<{ skills_count: number; duration_ms: number } | null>(null);
  const rebuildMutation = useRebuildRegistry();

  const handleRebuild = async () => {
    try {
      const result = await rebuildMutation.mutateAsync();
      setRebuildResult(result);
      refetch();
    } catch (e: any) {
      showToast(e?.response?.data?.detail ?? e?.message ?? 'Rebuild failed', 'error');
    }
  };

  const handleRemove = async (id: string) => {
    if (!confirm(`Remove source "${id}" and all its skills?`)) return;
    try {
      await skillService.removeSource(id);
      showToast(`Source "${id}" removed`, 'success');
      refetch();
      queryClient.invalidateQueries(['skills']);
    } catch (e: any) {
      showToast(e?.response?.data?.detail ?? e?.message ?? 'Remove failed', 'error');
    }
  };

  const handleSyncAll = async () => {
    setIsSyncingAll(true);
    try {
      const reports = await skillService.syncAll();
      setSyncReport(reports);
      const count = Array.isArray(reports) ? reports.length : 1;
      showToast(`Sync complete for ${count} source${count !== 1 ? 's' : ''}`, 'success');
      refetch();
      queryClient.invalidateQueries(['skills']);
    } catch (e: any) {
      showToast(e?.response?.data?.detail ?? e?.message ?? 'Sync All failed', 'error');
    } finally {
      setIsSyncingAll(false);
    }
  };

  const handleAddSource = async (req: any) => {
    await skillService.addSource(req);
    showToast(`Source "${req.name}" added`, 'success');
    refetch();
  };

  return (
    <div className={`container mx-auto p-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Skill Sources</h1>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Manage external skill repositories
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <AddSourceForm onAdd={handleAddSource} />
          <button
            onClick={handleSyncAll}
            disabled={isSyncingAll}
            className={`px-4 py-2 rounded-md text-sm font-medium text-white ${
              isSyncingAll ? 'opacity-50 cursor-not-allowed ' : ''
            }${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'}`}
          >
            {isSyncingAll ? 'Syncing...' : 'Sync All'}
          </button>
          <button
            onClick={handleRebuild}
            disabled={rebuildMutation.isLoading}
            title="Re-scan all skill directories and rebuild the in-memory registry from disk. Use this if counts look wrong after a sync or bulk delete."
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              rebuildMutation.isLoading
                ? 'opacity-50 cursor-not-allowed '
                : ''
            }${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
          >
            {rebuildMutation.isLoading ? 'Rebuilding…' : '↺ Rebuild Registry'}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-12"><Spinner size="lg" /></div>
      ) : error ? (
        <div className={`text-center py-12 text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
          Failed to load sources. Are you logged in as a superuser?
        </div>
      ) : (
        <SourcesTable sources={sources} onRemove={handleRemove} showToast={showToast} />
      )}

      {/* Rebuild result toast */}
      {rebuildResult && (
        <div className={`mt-4 px-4 py-3 rounded-lg text-sm flex items-center justify-between ${
          darkMode ? 'bg-green-900/40 text-green-300 border border-green-700' : 'bg-green-50 text-green-700 border border-green-200'
        }`}>
          <span>
            ✓ Registry rebuilt — <strong>{rebuildResult.skills_count}</strong> skills indexed in {rebuildResult.duration_ms}ms
          </span>
          <button onClick={() => setRebuildResult(null)} className="ml-4 opacity-60 hover:opacity-100">×</button>
        </div>
      )}

      <SyncReportModal
        isOpen={!!syncReport}
        onClose={() => setSyncReport(null)}
        report={syncReport}
      />
    </div>
  );
};
